import { useContext, useEffect, useReducer } from "react";
import { 
    Button, Dialog, DialogActions, DialogContent, DialogTitle, 
    FormControl, IconButton, List, ListItem, ListItemIcon, 
    ListItemText, MenuItem, TextField, Typography, Card, Box, Divider, Avatar
} from "@mui/material";
import { Facebook, Instagram, Twitter, YouTube, WhatsApp, Language, Edit } from '@mui/icons-material';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { LoadingButton } from "@mui/lab";

import { AddRedSocial, GetRedesSociales, UpdateRedSocial } from "../../../controllers/company/CompanyProfileController";
import UserContext from "../../../navigation/context/userContext";
import AuxAlert from "../../../components/alert";

// DECLARAMOS LOS ESTADOS INICIALES
const initialState = {
    confSocials: [],
    redesSociales: [],

    uuid: '',
    idRedSocial: '', errorIdRedSocial: false,
    enlace: '',      errorEnlace: false,
    username: '',    errorUsername: false,
    estatus: '',     errorEstatus: false,

    operacion: 'agregar',
    isLoading: false,
    open: false,

    // Para la alerta unificada
    openAlert: false,
    title: '',
    message: '',
    success: false
};
  
function reducer(state, { field, value }) {
    return { ...state, [field]: value };
}

// Función helper para obtener el ícono y color correcto según la red social
const getSocialIconAndColor = (id) => {
    switch (id) {
        case 8: return { icon: <Instagram />, color: '#E1306C', bg: '#fce4ec' };
        case 2: return { icon: <YouTube />, color: '#FF0000', bg: '#ffebee' };
        case 11: return { icon: <Facebook />, color: '#1877F2', bg: '#e3f2fd' };
        case 4: return { icon: <Twitter />, color: '#1DA1F2', bg: '#e1f5fe' };
        case 9: return { icon: <WhatsApp />, color: '#25D366', bg: '#e8f5e9' };
        default: return { icon: <Language />, color: '#757575', bg: '#f5f5f5' };
    }
};

export default function Socials() {
    const { userData } = useContext(UserContext);
    const [state, setState] = useReducer(reducer, initialState);

    const {
        confSocials, redesSociales, uuid,
        idRedSocial, errorIdRedSocial, enlace, errorEnlace, 
        username, errorUsername, estatus, errorEstatus,
        operacion, isLoading, open,
        openAlert, title, message, success
    } = state;

    useEffect(() => {
        handleGetRedesSociales();
    }, []);

    const onChange = (e) => {
        setState({ field: e.target.name, value: e.target.value });
    };

    const handleGetRedesSociales = async () => {
        let response = await GetRedesSociales();
        if (response.success === true) {
            setState({ field: 'confSocials', value: response.data.confSocials || [] });
            setState({ field: 'redesSociales', value: response.data.redesSociales || [] });
        }
    };

    const cancel = () => {
        setState({ field: 'enlace', value: '' });
        setState({ field: 'username', value: '' });
        setState({ field: 'idRedSocial', value: '' });
        setState({ field: 'estatus', value: '' });

        setState({ field: 'errorEnlace', value: false });
        setState({ field: 'errorUsername', value: false });
        setState({ field: 'errorIdRedSocial', value: false });
        setState({ field: 'errorEstatus', value: false });
    };

    const handleOpenAdd = () => {
        setState({ field: 'operacion', value: 'agregar' });
        cancel();
        setState({ field: 'open', value: true });
    };

    const handleOpenUpdate = (item) => {
        setState({ field: 'operacion', value: 'actualizar' });
        setState({ field: 'uuid', value: item.uuid });
        setState({ field: 'enlace', value: item.enlace });
        setState({ field: 'username', value: item.username });
        setState({ field: 'idRedSocial', value: item.idRedSocial });
        setState({ field: 'estatus', value: item.estatus });
        setState({ field: 'open', value: true });
    };

    const handleClose = () => {
        cancel();
        setState({ field: 'open', value: false });
    };

    const formVerification = () => {
        let ban = 0;
        if (idRedSocial === '') { setState({ field: 'errorIdRedSocial', value: true }); ban = 1; }
        if (enlace === '') { setState({ field: 'errorEnlace', value: true }); ban = 1; }
        if (username === '') { setState({ field: 'errorUsername', value: true }); ban = 1; }
        if (estatus === '') { setState({ field: 'errorEstatus', value: true }); ban = 1; }
        return ban;
    };

    const handleAddSubmit = async () => {
        if (formVerification() === 0) {
            setState({ field: 'isLoading', value: true });

            let response;
            if (operacion === 'agregar') {
                response = await AddRedSocial(idRedSocial, username, enlace, estatus);
            } else {
                response = await UpdateRedSocial(uuid, idRedSocial, username, enlace, estatus);
            }

            setState({ field: 'message', value: response.message });
            setState({ field: 'success', value: response.success });
            setState({ field: 'title', value: response.success ? 'Operación Exitosa' : 'Error' });
            setState({ field: 'openAlert', value: true });
            
            // Cierra la alerta después de unos segundos
            setTimeout(() => setState({ field: 'openAlert', value: false }), 5000);

            setState({ field: 'isLoading', value: false });
            if (response.success) {
                handleClose();
                handleGetRedesSociales();
            }
        }
    };

    // Estilo base unificado
    const cardStyle = { elevation: 0, sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: { xs: 2, sm: 4 }, bgcolor: '#ffffff', maxWidth: 800 } };

    return (
        <Card {...cardStyle}>
            {/* --- HEADER --- */}
            <Box display='flex' flexDirection={{ xs: 'column', sm: 'row' }} justifyContent='space-between' alignItems={{ xs: 'flex-start', sm: 'center' }} gap={2} mb={3}>
                <Box>
                    <Typography variant='h6' sx={{ fontWeight: 800, color: '#222' }}>Redes Sociales</Typography>
                    <Typography variant="body2" color="text.secondary">Agrega los enlaces a tus perfiles para que tus clientes te sigan.</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={handleOpenAdd}
                    disableElevation
                    sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                >
                    Agregar Enlace
                </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* --- LISTA DE REDES --- */}
            {redesSociales.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                    No has agregado ninguna red social a tu perfil.
                </Typography>
            ) : (
                <List disablePadding>
                    {redesSociales.map((item, index) => {
                        const styleConfig = getSocialIconAndColor(item.idRedSocial);
                        const isInactive = item.estatus === 0 || item.estatus === '0';
                        
                        return (
                            <Box key={item.id}>
                                <ListItem
                                    sx={{ 
                                        py: 1.5, px: 2, mb: 1, 
                                        border: '1px solid #f0f0f0', borderRadius: 2, 
                                        bgcolor: isInactive ? '#fafafa' : '#fff',
                                        opacity: isInactive ? 0.6 : 1
                                    }}
                                    secondaryAction={
                                        <IconButton edge='end' onClick={() => handleOpenUpdate(item)} sx={{ color: '#1976d2', bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}>
                                            <Edit fontSize="small" />
                                        </IconButton>
                                    }
                                >
                                    <ListItemIcon>
                                        <Avatar sx={{ bgcolor: styleConfig.bg, color: styleConfig.color, width: 40, height: 40 }}>
                                            {styleConfig.icon}
                                        </Avatar>
                                    </ListItemIcon>
                                    <ListItemText 
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body2" fontWeight="bold" color="#222">@{item.username}</Typography>
                                                {isInactive && <Typography variant="caption" sx={{ bgcolor: '#ffebee', color: '#c62828', px: 1, borderRadius: 1, fontWeight: 'bold' }}>Inactivo</Typography>}
                                            </Box>
                                        } 
                                        secondary={
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 300 }} noWrap>
                                                {item.enlace || item.url}
                                            </Typography>
                                        } 
                                    />
                                </ListItem>
                            </Box>
                        );
                    })}
                </List>
            )}

            {/* --- MODAL (NUEVA/EDITAR RED) --- */}
            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {operacion === 'agregar' ? 'Agregar Red Social' : 'Editar Red Social'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <FormControl fullWidth sx={{ mb: 2, mt: 1 }}>
                        <TextField
                            select
                            size="small"
                            name='idRedSocial'
                            label="Plataforma"
                            value={idRedSocial}
                            onChange={onChange}
                            onBlur={() => setState({ field: 'errorIdRedSocial', value: idRedSocial === '' })}
                            error={errorIdRedSocial}
                            helperText={errorIdRedSocial && 'Seleccione una plataforma'}
                        >
                            <MenuItem value=''>Seleccione una plataforma</MenuItem>
                            {confSocials.map((item) => (
                                <MenuItem key={item.id} value={item.id}>{item.nombre}</MenuItem>
                            ))}
                        </TextField>
                    </FormControl>

                    <TextField
                        name='username'
                        label="Nombre de Usuario"
                        placeholder="Ej: @mi_negocio"
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={username}
                        onChange={onChange}
                        onBlur={() => setState({ field: 'errorUsername', value: username === '' })}
                        error={errorUsername}
                        helperText={errorUsername && 'Ingrese el username'}
                    />

                    <TextField
                        name='enlace'
                        label="Enlace (URL completa)"
                        placeholder="https://instagram.com/mi_negocio"
                        size="small"
                        fullWidth
                        sx={{ mb: 2 }}
                        value={enlace}
                        onChange={onChange}
                        onBlur={() => setState({ field: 'errorEnlace', value: enlace === '' })}
                        error={errorEnlace}
                        helperText={errorEnlace && 'Ingrese la url del enlace'}
                    />
                    
                    <FormControl fullWidth>
                        <TextField
                            select
                            size="small"
                            name='estatus'
                            label="Estado"
                            value={estatus}
                            onChange={onChange}
                            onBlur={() => setState({ field: 'errorEstatus', value: estatus === '' })}
                            error={errorEstatus}
                            helperText={errorEstatus && 'Seleccione el estatus'}
                        >
                            <MenuItem value='1'>Activo</MenuItem>
                            <MenuItem value='0'>Inactivo</MenuItem>
                        </TextField>
                    </FormControl>
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
                    <Button disabled={isLoading} onClick={handleClose} sx={{ color: 'text.secondary', fontWeight: 'bold', textTransform: 'none' }}>
                        Cancelar
                    </Button>
                    <LoadingButton
                        loading={isLoading}
                        onClick={handleAddSubmit}
                        variant="contained"
                        disableElevation
                        sx={{ borderRadius: 2, fontWeight: 'bold', textTransform: 'none' }}
                    >
                        {operacion === 'agregar' ? 'Agregar' : 'Guardar Cambios'}
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            {/* --- ALERTA GLOBAL --- */}
            <AuxAlert open={openAlert} success={success} title={title} message={message} />
        </Card>
    );
}